/**
 * Failure prose meaning "the reset link itself is expired or already used", as
 * opposed to "the reset attempt failed". The two backend paths word it
 * differently: `POST /valid-token` (read by `isTokenValidGuard`) answers
 * "Invalid token", while `POST /reset-password` (the submit path) answers
 * "Token validation failed." Matching only the first is what previously made an
 * expired link render the generic "please try again" copy on the submit path —
 * advice that cannot work, because the backend token is single-use.
 *
 * Developer Note: this is a DELIBERATE, indefinite exception to the
 * `ApiResponse.errors` contract in `src/types/commons.ts` ("never key UI
 * behaviour on its text — key on `errorCode`"). The `errorCode` that would
 * replace it was halted backend-side on 2026-09-03 until further notice, so do
 * not treat this as a stopgap awaiting a swap, and do not delete it in favour
 * of a code that does not exist.
 *
 * The usual reason that contract exists does not apply to these two strings.
 * Prose is normally unsafe because `ErrorDisplayOptions:IncludeDiagnosticDetail`
 * makes it detailed in Development and generic in Production — but that flag is
 * read only by `MbrsController`, `ApiExceptionFilter` and the conversion
 * engines. Both phrases below are hardcoded literals in the backend's
 * `AuthService` (`Fail("Invalid token")` on the token-validation path,
 * `Fail("Token validation failed.")` on the reset path), so they are identical
 * in every environment. Verified 2026-09-03 against `yyc-backend-mbrs`.
 *
 * What WOULD break this: someone rewording either literal backend-side. The
 * failure is silent and degrades to the generic "please try again" screen, so
 * if that regresses, look there first.
 */
const INVALID_TOKEN_PHRASES = ['invalid token', 'token validation failed'];

/**
 * Whether a failed reset's `errors` mean the link is expired or already used
 * ("Expired" screen) rather than a retryable failure ("Failed" screen).
 *
 * Shared by `ResetPasswordStatusComponent` (which screen to render) and
 * `ResetPasswordComponent` (whether a sibling tab should follow the outcome),
 * so the two can never disagree about what counts as "Expired".
 *
 * @param messages `ApiResponse.errors`, already passed through `normalizeApiErrors`.
 */
export function isInvalidTokenFailure(messages: string[]): boolean {
  return messages.some((message) =>
    INVALID_TOKEN_PHRASES.some((phrase) => message.toLowerCase().includes(phrase))
  );
}
